import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIntake } from '../../context/IntakeContext';
import { Textarea } from '../ui/Textarea';
import { Input } from '../ui/Input';
import { CheckboxGroup } from '../ui/CheckboxGroup';
import { MascotPanel } from '../ui/MascotPanel';
import { Button } from '../ui/Button';
import { Tooltip } from '../ui/Tooltip';
import { PlusIcon, Trash2Icon, InfoIcon } from 'lucide-react';
export function Step5Story() {
  const {
    data,
    updateData
  } = useIntake();
  const addService = () => {
    const newService = {
      id: Date.now().toString(),
      name: '',
      description: ''
    };
    updateData({
      services: [...data.services, newService]
    });
  };
  const removeService = (id: string) => {
    updateData({
      services: data.services.filter(s => s.id !== id)
    });
  };
  const updateService = (id: string, field: string, value: string) => {
    updateData({
      services: data.services.map(s => s.id === id ? {
        ...s,
        [field]: value
      } : s)
    });
  };
  const feelOptions = [{
    value: 'professional',
    label: 'Professioneel & Zakelijk'
  }, {
    value: 'modern',
    label: 'Modern & Innovatief'
  }, {
    value: 'luxury',
    label: 'Luxe & Premium'
  }, {
    value: 'approachable',
    label: 'Toegankelijk & Vriendelijk'
  }, {
    value: 'expert',
    label: 'Autoriteit & Expert'
  }, {
    value: 'playful',
    label: 'Speels & Creatief'
  }, {
    value: 'calm',
    label: 'Rustig & Betrouwbaar'
  }, {
    value: 'bold',
    label: 'Stoer & Opvallend'
  }, {
    value: 'other',
    label: 'Anders...'
  }];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="max-w-3xl mx-auto space-y-8" data-id="element-419">
      <MascotPanel message="Nu wordt het leuk! Vertel me meer over het DNA van uw bedrijf. Wat maakt u uniek? Deze input is cruciaal voor het design en de teksten." data-id="element-420" />

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-8 space-y-8" data-id="element-421">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-422">
          Bedrijfsverhaal & Positionering
        </h2>

        <div data-id="element-423">
          <div className="flex items-center mb-3" data-id="element-424">
            <label className="block text-sm font-semibold text-gray-900" data-id="element-425">
              Uw diensten of producten
            </label>
            <Tooltip content="Voeg uw belangrijkste diensten of producten toe. Dit helpt ons de juiste paginastructuur, content en SEO silo's te bepalen." data-id="element-426">
              <button type="button" className="ml-2 text-gray-400 hover:text-[#007AFF] focus:outline-none" data-id="element-427">
                <InfoIcon className="w-4 h-4" data-id="element-428" />
              </button>
            </Tooltip>
          </div>

          <div className="space-y-4" data-id="element-429">
            {data.services.map((service, index) => <motion.div key={service.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 relative" data-id="element-430">
                <button onClick={() => removeService(service.id)} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" data-id="element-431">
                  <Trash2Icon className="w-4 h-4" data-id="element-432" />
                </button>

                <h4 className="font-semibold text-gray-900 mb-3 text-sm" data-id="element-433">
                  Dienst {index + 1}
                </h4>

                <div className="space-y-3" data-id="element-434">
                  <Input label="Naam dienst / product" placeholder="Bijv. Belastingadvies, Loodgieter spoedservice, Webdesign..." value={service.name} onChange={e => updateService(service.id, 'name', e.target.value)} data-id="element-435" />
                  <Textarea label="Korte beschrijving" placeholder="Wat houdt deze dienst in? Voor wie is het? Wat maakt het bijzonder?" value={service.description} onChange={e => updateService(service.id, 'description', e.target.value)} className="min-h-[80px]" data-id="element-436" />
                </div>
              </motion.div>)}

            <Button variant="outline" onClick={addService} className="w-full border-dashed" data-id="element-437">
              <PlusIcon className="w-5 h-5 mr-2" data-id="element-438" />
              Dienst / Product toevoegen
            </Button>
          </div>
        </div>

        <Textarea label="Wat maakt u anders dan concurrenten?" infoTooltip="Denk aan uw Unique Selling Points (USP's). Waarom kiezen klanten voor u en niet voor de buurman?" placeholder="Bijv. Wij bieden 24/7 service, hebben 20 jaar ervaring, of gebruiken een unieke methode..." value={data.difference} onChange={e => updateData({
        difference: e.target.value
      })} data-id="element-439" />

        <Textarea label="Wat zijn uw kernwaarden?" placeholder="Bijv. Transparant, Duurzaam, Klantgericht, Snel..." value={data.coreValues} onChange={e => updateData({
        coreValues: e.target.value
      })} data-id="element-440" />

        <div data-id="element-441">
          <label className="block text-sm font-semibold text-gray-900 mb-3" data-id="element-442">
            Hoe moet het bedrijf aanvoelen voor een bezoeker?
            <span className="text-gray-400 font-normal ml-1" data-id="element-443">(max 3)</span>
          </label>
          <CheckboxGroup columns={2} options={feelOptions} values={data.companyFeel} onChange={vals => {
          if (vals.length <= 3) updateData({
            companyFeel: vals
          });
        }} data-id="element-444" />

          <AnimatePresence data-id="element-445">
            {data.companyFeel.includes('other') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="mt-4 overflow-hidden" data-id="element-446">
                <Input placeholder="Vul hier in hoe het bedrijf moet aanvoelen..." value={data.companyFeelOther} onChange={e => updateData({
              companyFeelOther: e.target.value
            })} data-id="element-447" />
              </motion.div>}
          </AnimatePresence>
        </div>

        <Textarea label="Zijn er woorden of thema's die we absoluut moeten vermijden?" placeholder="Bijv. Gebruik het woord 'goedkoop' niet, wij zijn 'voordelig'." value={data.mustAvoid} onChange={e => updateData({
        mustAvoid: e.target.value
      })} data-id="element-448" />

        <div className="pt-6 border-t border-gray-100" data-id="element-449">
          <Textarea label="Beschrijf uw ideale website in 2-3 zinnen" infoTooltip="Dit helpt ons (en onze AI) om precies te begrijpen welke richting u op wilt met het design en de sfeer van de website." placeholder="Bijv. Een strakke, moderne site die vertrouwen wekt en bezoekers snel naar het contactformulier leidt..." value={data.idealWebsiteDescription} onChange={e => updateData({
          idealWebsiteDescription: e.target.value
        })} data-id="element-450" />
        </div>
      </div>
    </motion.div>;
}